import { Link } from 'react-router-dom';
import { useEarlyAccess } from '../EarlyAccessContext';
import ContactForm from '../components/ContactForm';
import NetworkVisual from '../components/NetworkVisual';
import aboutImg from '../assets/img/about-as-networks.png';
import expertImg from '../assets/img/expert-engineers.png';
import consultingImg from '../assets/img/consulting-services.avif';
import tailoredImg from '../assets/img/tailored-solutions.avif';

function HomePage() {
  const openEarlyAccess = useEarlyAccess();

  return (
    <>
      <div className="hero-header sec-padding">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <span className="hero-badge">Launching July–August 2026 — Limited to 5 Pilot Customers</span>
              <h1>
                Network Monitoring,{' '}
                <span className="text-gradient">Simplified</span>
              </h1>
              <p className="mb-4 pb-2">
                Netwatch gives you complete visibility into your entire network infrastructure.
                Discover devices automatically, monitor health in real time, and control access with
                enterprise-grade security — supporting <b>20+ vendor families</b> out of the box.
              </p>
              <div className="banner-buttons">
                <button onClick={openEarlyAccess} className="btn-primary-gradient">Join Early Access</button>
                <Link to="/product" className="btn-outline-white">Learn More</Link>
              </div>
            </div>
            <div className="col-lg-6">
              <NetworkVisual variant="hero" />
            </div>
          </div>
        </div>
      </div>

      <div className="stats-section">
        <div className="container">
          <div className="row">
            <div className="col-6 col-md-3">
              <div className="stat-item">
                <div className="stat-number">500+</div>
                <div className="stat-label">Networks Monitored</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="stat-item">
                <div className="stat-number">99.9%</div>
                <div className="stat-label">Uptime SLA</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="stat-item">
                <div className="stat-number">20+</div>
                <div className="stat-label">Vendor Families</div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="stat-item">
                <div className="stat-number">10+</div>
                <div className="stat-label">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sec-padding" style={{ background: '#f8f9fc' }}>
        <div className="container">
          <div className="text-center mb-4">
            <span className="section-subtitle">Product Features</span>
          </div>
          <h2 className="head-main after-border center">Why Teams Choose Netwatch</h2>
          <div className="row g-4">
            <div className="col-lg-4 col-md-6">
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fas fa-search-location"></i>
                </div>
                <h3>Automatic Network Discovery</h3>
                <p>Discover every device on your network automatically — routers, switches, firewalls, servers, wireless APs, and more. Know exactly what's connected and how it's configured.</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fas fa-chart-line"></i>
                </div>
                <h3>Real-time Monitoring</h3>
                <p>Live dashboards showing DNS queries, DHCP leases, IP utilization, and device health across your entire infrastructure in one unified view.</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fas fa-user-shield"></i>
                </div>
                <h3>Role-Based Access Control</h3>
                <p>Five role tiers from Super Admin to Guest with granular permissions. The interface adapts to each user's role — showing only what they need to see.</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fas fa-bell"></i>
                </div>
                <h3>Smart Alerts</h3>
                <p>Customizable alert thresholds with multi-channel notifications — email, Slack, webhooks, and more. Stay ahead of issues before they become outages.</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fas fa-chart-bar"></i>
                </div>
                <h3>Analytics &amp; Reporting</h3>
                <p>Historical trends, capacity planning, and performance reports to make data-driven decisions about your network infrastructure.</p>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="feature-card">
                <div className="feature-icon">
                  <i className="fas fa-plug"></i>
                </div>
                <h3>Multi-vendor Support</h3>
                <p>Works with 20+ vendor families including Cisco, Juniper, Palo Alto, Checkpoint, FortiGate, Arista, and Microsoft — firewall monitoring via SNMP, no vendor lock-in.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sec-padding">
        <div className="container">
          <div className="text-center mb-4">
            <span className="section-subtitle">How It Works</span>
          </div>
          <h2 className="head-main after-border center">Up and Running in Minutes</h2>
          <div className="row g-4">
            <div className="col-lg-3 col-md-6">
              <div className="step-card">
                <div className="step-number">1</div>
                <h3>Discover</h3>
                <p>Point Netwatch at your network and it automatically discovers every device — identifying vendors, models, capabilities, and connections.</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="step-card">
                <div className="step-number">2</div>
                <h3>Auto Template Suggestion</h3>
                <p>Netwatch analyses each discovered device and automatically suggests the right monitoring templates — so you're collecting the correct SNMP metrics from day one, with zero manual configuration.</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="step-card">
                <div className="step-number">3</div>
                <h3>Monitor</h3>
                <p>Get instant visibility with real-time dashboards, health checks, and automated anomaly detection across your entire infrastructure.</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="step-card">
                <div className="step-number">4</div>
                <h3>Optimize</h3>
                <p>Use actionable insights and analytics to improve performance, reduce downtime, and plan capacity with confidence.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid why-choose-us sec-padding" style={{ background: '#f8f9fc' }}>
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-1"></div>
            <div className="col-lg-5">
              <img src={aboutImg} alt="About NexVeda Networks" />
            </div>
            <div className="col-lg-5">
              <div className="box-offer">
                <span className="section-subtitle">About Us</span>
                <h2 className="head-main after-border">Built by Network Experts</h2>
                <p>
                  NexVeda Networks was founded with a vision to empower organizations with better network management tools.
                  With over a decade of hands-on experience across enterprise networks, we built Netwatch to solve the problems we saw
                  our clients face every day — and we still offer expert consulting alongside our product.
                </p>
                <div className="banner-buttons">
                  <Link to="/about" className="btn-blue">Learn More</Link>
                </div>
              </div>
            </div>
            <div className="col-lg-1"></div>
          </div>
        </div>
      </div>

      <div className="sec-padding">
        <div className="container">
          <div className="text-center mb-4">
            <span className="section-subtitle">Expert Services</span>
          </div>
          <h2 className="head-main after-border center">Product + Services, Together</h2>
          <p className="text-center mb-5" style={{ maxWidth: '700px', margin: '0 auto 40px', color: '#6b7280' }}>
            Beyond Netwatch, our team of network experts provides hands-on consulting, implementation, and training services.
          </p>
          <div className="row align-items-start g-4">
            <div className="col-lg-4">
              <div className="service-item" style={{ background: '#fff', borderRadius: '12px', border: '1px solid #f0f0f5', overflow: 'hidden', height: '100%', transition: 'all 0.4s ease' }}>
                <div className="service-img" style={{ overflow: 'hidden' }}>
                  <img src={expertImg} alt="Expert Engineers" style={{ height: '240px', objectFit: 'cover', transition: 'all 0.5s ease' }} />
                </div>
                <div className="p-4">
                  <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#0e63ff', marginBottom: '12px' }}>Expert Engineers</h3>
                  <p style={{ fontSize: '15px', color: '#6b7280' }}>Our team integrates seamlessly with yours, offering hands-on network infrastructure support and expertise.</p>
                  <div className="banner-buttons">
                    <Link to="/services" className="btn-blue">Learn More</Link>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="service-item" style={{ background: '#fff', borderRadius: '12px', border: '1px solid #f0f0f5', overflow: 'hidden', height: '100%', transition: 'all 0.4s ease' }}>
                <div className="service-img" style={{ overflow: 'hidden' }}>
                  <img src={consultingImg} alt="Consulting Services" style={{ height: '240px', objectFit: 'cover', transition: 'all 0.5s ease' }} />
                </div>
                <div className="p-4">
                  <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#0e63ff', marginBottom: '12px' }}>Consulting Services</h3>
                  <p style={{ fontSize: '15px', color: '#6b7280' }}>Strategic guidance on network optimization, automation, and best practices for your infrastructure.</p>
                  <div className="banner-buttons">
                    <Link to="/services" className="btn-blue">Learn More</Link>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="service-item" style={{ background: '#fff', borderRadius: '12px', border: '1px solid #f0f0f5', overflow: 'hidden', height: '100%', transition: 'all 0.4s ease' }}>
                <div className="service-img" style={{ overflow: 'hidden' }}>
                  <img src={tailoredImg} alt="Tailored Solutions" style={{ height: '240px', objectFit: 'cover', transition: 'all 0.5s ease' }} />
                </div>
                <div className="p-4">
                  <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#0e63ff', marginBottom: '12px' }}>Tailored Solutions</h3>
                  <p style={{ fontSize: '15px', color: '#6b7280' }}>Custom implementations designed to address your organization's unique challenges and goals.</p>
                  <div className="banner-buttons">
                    <Link to="/services" className="btn-blue">Learn More</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="cta-section text-center">
        <div className="container position-relative" style={{ zIndex: 1 }}>
          <h2>Be Among the First to Experience Netwatch</h2>
          <p>Our MVP launches July–August 2026 with select pilot customers. Get hands-on access, shape the product, and gain a competitive edge — limited to 5 spots.</p>
          <div className="banner-buttons" style={{ justifyContent: 'center' }}>
            <button onClick={openEarlyAccess} className="btn-primary-gradient">Apply for Early Access</button>
            <a href="#demo-form" className="btn-outline-white">Talk to an Expert</a>
          </div>
        </div>
      </div>

      <ContactForm earlyAccess={true} title="Apply for Early Access" description="Our MVP launches July–August 2026. We're selecting 5 pilot customers to test Netwatch and help shape its future. Fill out the form below and our team will be in touch." />
    </>
  );
}

export default HomePage;
